import { Card, Grid } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Calendar from '@fullcalendar/react'
import queryString from 'query-string'
import CalendarFilter, {
  EViewType,
  IFilterMapped,
} from './components/CalendarFilter'
import dayjs from 'dayjs'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import timeGridPlugin from '@fullcalendar/timegrid'
import timelinePlugin from '@fullcalendar/timeline'

import PageUrls from '../../../../../../constants/page-urls'
import { ContentWrapper } from '../../../../../../components/ContentWrapper'
import Helmet from '../../../../../../components/Helmet'
import HeaderContent from '../../../../../../components/Layouts/HeaderContent'
import { DATE_FORMAT_INTERNATIONAL } from '../../../../../../constants/global'
import { CalendarContainer } from './components/CalendarContainer'
import { CalendarTableModal } from './components/CalendarTableModal'
import { axiosInstance } from '../../../../../../hooks/useAxios'
import { ITrackingNotificationsGetCalendar } from '../../../../../../types/trackingNotification'

const Menus = [
  {
    breadcrumbName: 'รายการติดตามทั้งหมด',
    title: 'รายการติดตามทั้งหมด',
    path: PageUrls.CRM.Lead.List,
  },
]

const LeadCalenderPage = () => {
  const navigate = useNavigate()
  const [isModalCalendarTable, setIsModalCalendarTable] =
    useState<boolean>(false)
  const [dateStatusSelect, setDateStatusSelect] = useState<{
    startDate: string
    endDate: string
    status: string
  }>()
  const queryStr = queryString.parse(window.location.search)
  const calendarRef = useRef<any>(null)
  const [date, setDate] = useState<Date>(new Date())
  const [view, setView] = useState<EViewType>(EViewType.MONTH)
  const [events, setEvents] = useState<{ title: string; date: string }[]>([])
  const [filter, setFilter] = useState<IFilterMapped>({
    page: Number(queryStr.page ?? 1),
    perPage: 1000,
    startDate: queryStr.startDate
      ? dayjs().startOf('day').format()
      : undefined,
    endDate: queryStr.endDate
      ? dayjs().endOf('day').format()
      : undefined,
  })

  useEffect(() => {
    setDate(dayjs(filter.startDate).startOf('month').toDate())
  }, [filter.startDate])

  const onFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      const newQuery = queryString.stringify({ ...newFilter })
      // navigate(`${PageUrls.CRM.Lead.Calender}?${newQuery}`, {
      //   replace: true,
      // })
      setFilter(newFilter)
    },
    [filter],
  )

  useEffect(() => {
    onFilterChange(filter)
    getEvents()
  }, [filter])

  const getEvents = useCallback(async () => {
    try {
      const response = await axiosInstance.get<
        ITrackingNotificationsGetCalendar[]
      >('/lead/calendar', {
        params: {
          startDate: dayjs(
            filter?.startDate ?? dayjs().startOf('month'),
          ).format(DATE_FORMAT_INTERNATIONAL),
          endDate: dayjs(filter?.endDate ?? dayjs().endOf('month')).format(
            DATE_FORMAT_INTERNATIONAL,
          ),
        },
      })
      const d: any[] = []
      response.data?.map((item: ITrackingNotificationsGetCalendar) => {
        d.push({
          title: `ทั้งหมด ${item.count ?? 0} รายการ`,
          date: dayjs(item?.date).format(DATE_FORMAT_INTERNATIONAL),
          color: '#104bb9',
          sort: 0,
          data: {
            date: item?.date,
            status: 'all',
          },
        })
        d.push({
          title: `ติดตามแล้ว ${item.success ?? 0} รายการ`,
          date: dayjs(item?.date).format(DATE_FORMAT_INTERNATIONAL),
          color: '#10B981',
          sort: 1,
          data: {
            date: item?.date,
            status: 'success',
          },
        })
        d.push({
          title: `ยังไม่ติดตาม ${item.pending ?? 0} รายการ`,
          date: dayjs(item?.date).format(DATE_FORMAT_INTERNATIONAL),
          color: '#f04438',
          sort: 2,
          data: {
            date: item?.date,
            status: 'pending',
          },
        })
      })
      setEvents(d)
    } catch (error) {
      console.error(error)
    }
  }, [filter])

  const handleViewChange = useCallback((view: EViewType) => {
    const calendarEl = calendarRef.current

    if (calendarEl) {
      const calendarApi = calendarEl.getApi()

      calendarApi.changeView(view)
      setView(view)
    }
  }, [])

  const handleDatePrev = useCallback(() => {
    const calendarEl = calendarRef.current

    if (calendarEl) {
      const calendarApi = calendarEl.getApi()

      calendarApi.prev()
      setDate(calendarApi.getDate())
      const d = calendarApi.getDate()
      onFilterChange({
        ...filter,
        startDate: dayjs(String(d)).startOf('month').format(),
        endDate: dayjs(String(d)).endOf('month').format(),
      })
    }
  }, [])

  const handleDateNext = useCallback(() => {
    const calendarEl = calendarRef.current

    if (calendarEl) {
      const calendarApi = calendarEl.getApi()

      calendarApi.next()
      setDate(calendarApi.getDate())
      const d = calendarApi.getDate()
      onFilterChange({
        ...filter,
        startDate: dayjs(String(d)).startOf('month').format(),
        endDate: dayjs(String(d)).endOf('month').format(),
      })
    }
  }, [])

  const handleEventSelect = (info: any) => {
    const event = info.event
    const customData = event.extendedProps.data
    setDateStatusSelect({
      startDate: `${customData?.date}T00:00:00+07:00`,
      endDate: `${customData?.date}T23:59:59+07:00`,
      status: customData?.status,
    })
    setIsModalCalendarTable(true)
  }

  const handleEventDrop = () => {}

  const handleRangeSelect = async (data: any) => {
    const dateStart = dayjs(data.startStr).format(DATE_FORMAT_INTERNATIONAL)
    const dateEnd = dayjs(data.endStr).format(DATE_FORMAT_INTERNATIONAL)
    setDateStatusSelect({
      startDate: dayjs(dateStart).startOf('day').format(),
      endDate: dayjs(dateStart).endOf('day').format(),
      status: 'all',
    })

    setIsModalCalendarTable(true)
  }

  return (
    <ContentWrapper>
      <Helmet title="ปฎิทินติดตามลูกค้า" />
      <CalendarTableModal
        isOpen={isModalCalendarTable}
        onClose={(visible) => setIsModalCalendarTable(visible)}
        status={dateStatusSelect?.status!}
        date={dateStatusSelect!}
      />
      <Grid container justifyContent="flex-start" alignItems="center">
        <Grid item>
          <HeaderContent
            goBack
            title="ปฎิทินติดตามลูกค้า"
            routes={Menus}
            descriptions="ปฎิทินติดตามลูกค้า"
          />
        </Grid>
      </Grid>
      <Card
        sx={{
          padding: 2,
        }}
      >
        <CalendarFilter
          date={date}
          onDateNext={handleDateNext}
          onDatePrev={handleDatePrev}
          onViewChange={handleViewChange}
          view={view}
        />
        <CalendarContainer>
          <Calendar
            allDayMaintainDuration
            dayMaxEventRows={3}
            droppable={false}
            editable={false}
            eventClick={(e) => handleEventSelect(e)}
            eventDisplay="block"
            eventDrop={handleEventDrop}
            eventResizableFromStart
            eventOrder={`eventProperty`}
            // eventResize={handleEventResize}
            events={events}
            headerToolbar={false}
            height={900}
            initialDate={date}
            initialView={EViewType.MONTH}
            plugins={[
              dayGridPlugin,
              interactionPlugin,
              listPlugin,
              timeGridPlugin,
              timelinePlugin,
            ]}
            ref={calendarRef}
            rerenderDelay={10}
            select={(e) => handleRangeSelect(e)}
            selectable
            weekends
          />
        </CalendarContainer>
      </Card>
    </ContentWrapper>
  )
}

export default LeadCalenderPage
